import React from 'react';

const Publications: React.FC = () => {
  const publicationsData = [
    {
      section: "Peer-Reviewed Publications",
      entries: [
        {
          title: "Towards a digital twin for smart resilient cities: real-time fire and smoke tracking and prediction platform for community awareness (FireCom)",
          authors:
            "Kijin Seong, Junfeng Jiao, Ryan Hardesty Lewis, Arya Farahi, Paul Navrátil, Nate Casebeer, Braniff Davis, Justice Jones, Dev Niyogi",
          venue: "Computational Urban Science, 5(1):49",
          date: "October 2025",
          links: [
            { label: "Springer", href: "https://link.springer.com/article/10.1007/s43762-025-00212-x" },
          ],
          description:
            "Introduces FireCom, an urban fire-and-smoke digital twin that fuses air quality, meteorology, and 3D infrastructure data to support emergency response and public-facing dashboards.",
        },
        {
          title:
            "Developing a healthy food access index (HFAI): Web-based mapping and future directions for AI integrations",
          authors:
            "Junfeng Jiao, Kijin Seong, Marcus Sammer, Ryan Hardesty Lewis, Alison Reese, Norma Olvera, Susie L. Gronseth, Elizabeth Anderson-Fletcher, Ioannis Kakadiaris",
          venue: "Cities, Volume 161, 105908",
          date: "June 2025",
          links: [
            { label: "DOI", href: "https://doi.org/10.1016/j.cities.2025.105908" },
          ],
          description:
            "Defines the Healthy Food Access Index and the AI-FEED web platform to map inequities across Houston and guide AI-driven interventions for food resilience.",
        },
        {
          title: "Voxelizing Google Earth: A Pipeline for New Virtual Worlds",
          authors: "Ryan Hardesty Lewis",
          venue: "SIGGRAPH 2024",
          date: "July 2024",
          links: [
            { label: "DOI", href: "https://doi.org/10.1145/3641236.3664423" },
          ],
          description: "Creating a pipeline to voxelize Google Earth into Minecraft-compatible formats.",
        },
        {
          title: "Crowdsourced Streetview: Integrating Real-Time Imagery Updates into Google Streetview",
          authors: "Ryan Hardesty Lewis",
          venue: "SIGGRAPH 2024",
          date: "August 2024",
          links: [
            { label: "DOI", href: "https://doi.org/10.1145/3641234.3671067" },
          ],
          description: "An algorithm that overlays real-time social media imagery on Google Streetview.",
        },
        {
          title: "Fire and Smoke Digital Twin: A Computational Framework for Modeling Fire Incident Outcomes",
          authors: "Ryan Hardesty Lewis, Junfeng Jiao, Kijin Seong, et al.",
          venue: "Computers, Environment and Urban Systems, 110, 102093",
          date: "June 2024",
          links: [
            { label: "DOI", href: "https://doi.org/10.1016/j.compenvurbsys.2024.102093" },
          ],
          description: "Simulating 911 fire call outcomes using fluid simulations displayed in real-time.",
        },
      ],
    },
    {
      section: "Workshop Publications",
      entries: [
        {
          title: "Streaming the Future of Sustainability",
          authors: "Ryan Hardesty Lewis, Alex Conway",
          venue: "ACM SIGEnergy Energy Informatics Review (EIR), Volume 4, Issue 5; Presented at HotCarbon 2024",
          date: "July 2024",
          links: [
            { label: "PDF", href: "https://hotcarbon.org/assets/2024/pdf/hotcarbon24-final81.pdf" },
          ],
          description: "Discussing the role of streaming in reducing the carbon footprint with edge/cloud systems.",
        },
      ],
    },
    {
      section: "Posters and Invited Talks",
      entries: [
        {
          title: "Voxel Earth: Leveraging Digital Twins for Geospatial Analysis",
          venue: "SIGGRAPH CARTO BoF",
          date: "July 2024",
          links: [
            { label: "Slides", href: "https://docs.google.com/presentation/u/2/d/1ZyVmTKNdH1yW6r0lv3PFpQ3pYJjdDGJIjqihFlYVxzw/edit?usp=sharing" },
          ],
        },
        {
          title: "Building Cities Twice: The Making of Digital Twins",
          venue: "Smart Cities and Generative AI Symposium",
          date: "August 2023",
          links: [
            { label: "Slides", href: "https://hardesty.ai/assets/slides/buildingcitiestwice.pdf" },
          ],
        },
        {
          title: "Analyzing Downwind Smoke Impacts with Fluid Models",
          venue: "CHEER Climate Change and Health Symposium Poster Session",
          date: "February 2023",
          links: [
            { label: "Poster", href: "https://hardesty.ai/assets/posters/good-systems-poster-fire-dt.pdf" },
          ],
        },
      ],
    },
    {
      section: "Technical Articles",
      entries: [
        {
          title: "ComputeGPT: A Computational Chat Model for Numerical Problems",
          authors: "Ryan Hardesty Lewis, Junfeng Jiao",
          venue: "arXiv preprint",
          date: "March 2023",
          links: [
            { label: "arXiv", href: "https://arxiv.org/abs/2305.06223" },
          ],
          description: "One of the first implementations of automated code execution from language models.",
        },
        {
          title: "Gamifying Ethics for AI",
          authors: "Ryan Hardesty Lewis, Samuel Baker",
          venue: "arXiv preprint",
          date: "July 2022",
          links: [
            { label: "Website", href: "https://ethical.pages.dev/#" },
          ],
          description: "Embedding ethics in games by creating 'moral agents' using modified RL algorithms.",
        },
      ],
    },
  ];

  return (
    <section id="publications" className="bg-gray-50 dark:bg-gray-900 py-20">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8">
          Publications
        </h1>

        {publicationsData.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-12">
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
              {section.section}
            </h2>
            <div className="space-y-3">
              {section.entries.map((entry, entryIndex) => (
                <div
                  key={entryIndex}
                  className="p-3 bg-white dark:bg-gray-800 rounded-md shadow-sm hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {entry.title}
                  </h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1 italic">
                    {entry.venue}, {entry.date}
                  </p>
                  {entry.authors && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      {entry.authors}
                    </p>
                  )}
                  {entry.description && (
                    <p className="text-xs text-gray-700 dark:text-gray-300 mb-2">
                      {entry.description}
                    </p>
                  )}
                  <div className="mt-1 space-x-2">
                    {entry.links.map((link, linkIndex) => (
                      <a
                        key={linkIndex}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Publications;
